import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from '../../icons';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { FLOATING_CHIP_GLASS } from '../../styles/floating-bar';
import { CHROME_GLASS_INTENSITY } from '../../styles/glass';
import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import { Button } from '../Button';
import { GlassBackground } from '../Glass';
import type { PaginationDotsProps, PaginationProps } from './Pagination.types';
import {
  paginationDotsVariants,
  paginationDotVariants,
  paginationLabelTextVariants,
  paginationLabelVariants,
} from './Pagination.variants';

/**
 * The Figma "Icon Button" nested in the pagination frames renders a 20px
 * chevron inside the 40px (size `m`) chip — larger than Button's standard
 * 16px `m` glyph, same override pattern as ToggleButton's icon ramp.
 */
const PAGINATION_ICON_SIZE = 20;

/**
 * Cross-platform standard Pagination — Figma "Pagination standard"
 * (node 2348:15727 light / 3233:12804 dark). First / previous chevron
 * buttons, a flexible "page/total" label chip, next / last chevron buttons.
 *
 * The chevron buttons are the shared `<Button variant="secondary" size="m">`
 * icon-only chips — press/hover/focus states, the disabled `!opacity-30`
 * treatment at the range boundaries, and dark mode all come from Button.
 *
 * Controlled-only — the caller owns the page:
 *
 * @example
 * ```tsx
 * import { Pagination } from '@flashka/shared/ui';
 *
 * const [page, setPage] = useState(1);
 * <Pagination page={page} totalPages={52} onPageChange={setPage} />
 * ```
 */
export function Pagination({
  page,
  totalPages,
  onPageChange,
  labels,
  lastPage,
  edgeButtons = true,
  labelGlass = false,
  className,
  testID,
  webProps,
  nativeProps,
}: PaginationProps) {
  const atStart = page <= 1;
  const atEnd = page >= totalPages;
  // Last-chevron target — `lastPage` lets a caller point "last" at a
  // semantic end before the final page (see PaginationProps.lastPage).
  // The button disables only when already ON the target: past it, pressing
  // it jumps back.
  const lastTarget = Math.min(Math.max(lastPage ?? totalPages, 1), totalPages);
  const atLast = page === lastTarget;

  const go = (target: number) => {
    const clamped = Math.min(Math.max(target, 1), totalPages);
    if (clamped !== page) onPageChange?.(clamped);
  };

  return (
    <View
      className={cn('w-full flex-row items-center gap-2', className)}
      testID={testID}
      webProps={webProps}
      nativeProps={nativeProps}>
      {edgeButtons ? (
        <Button
          variant="secondary"
          size="m"
          icon={ChevronFirst}
          iconSize={PAGINATION_ICON_SIZE}
          disabled={atStart}
          onPress={() => go(1)}
          accessibilityLabel={labels?.first ?? 'First page'}
          testID={subTestID(testID, 'first')}
        />
      ) : null}
      <Button
        variant="secondary"
        size="m"
        icon={ChevronLeft}
        iconSize={PAGINATION_ICON_SIZE}
        disabled={atStart}
        onPress={() => go(page - 1)}
        accessibilityLabel={labels?.previous ?? 'Previous page'}
        testID={subTestID(testID, 'prev')}
      />
      <View
        className={cn(
          paginationLabelVariants(),
          labelGlass && `overflow-hidden ${FLOATING_CHIP_GLASS}`,
        )}>
        {labelGlass ? (
          // Native's frost (web's is the class's backdrop blur); the chip's
          // 14px corner minus its 1px border.
          <GlassBackground
            intensity={CHROME_GLASS_INTENSITY}
            radius={13}
            frostOnAndroid={false}
          />
        ) : null}
        <Text
          testID={subTestID(testID, 'label')}
          className={paginationLabelTextVariants({ tone: 'current' })}>
          {page}
          <Text className={paginationLabelTextVariants({ tone: 'total' })}>
            /{totalPages}
          </Text>
        </Text>
      </View>
      <Button
        variant="secondary"
        size="m"
        icon={ChevronRight}
        iconSize={PAGINATION_ICON_SIZE}
        disabled={atEnd}
        onPress={() => go(page + 1)}
        accessibilityLabel={labels?.next ?? 'Next page'}
        testID={subTestID(testID, 'next')}
      />
      {edgeButtons ? (
        <Button
          variant="secondary"
          size="m"
          icon={ChevronLast}
          iconSize={PAGINATION_ICON_SIZE}
          disabled={atLast}
          onPress={() => go(lastTarget)}
          accessibilityLabel={labels?.last ?? 'Last page'}
          testID={subTestID(testID, 'last')}
        />
      ) : null}
    </View>
  );
}
(Pagination as { displayName?: string }).displayName = 'Pagination';

/**
 * Cross-platform dot Pagination indicator — Figma "Pagination indicator"
 * (node 2350:1020 light / 3233:12841 dark). A row of 8px dots in a soft
 * bordered pill; the active dot is the saturated `-500` shade. Purely
 * presentational — pair with a swipeable carousel / stepper that owns the
 * index.
 *
 * @example
 * ```tsx
 * import { PaginationDots } from '@flashka/shared/ui';
 *
 * <PaginationDots count={5} activeIndex={step} accessibilityLabel={`Step ${step + 1} of 5`} />
 * <PaginationDots count={3} activeIndex={0} variant="secondary" />
 * ```
 */
export function PaginationDots({
  count,
  activeIndex,
  variant = 'primary',
  accessibilityLabel,
  className,
  testID,
  webProps,
  nativeProps,
}: PaginationDotsProps) {
  return (
    <View
      className={cn(paginationDotsVariants({ variant }), className)}
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      webProps={webProps}
      nativeProps={nativeProps}>
      {Array.from({ length: count }, (_, i) => (
        <View
          key={i}
          testID={subTestID(testID, `dot-${i}`)}
          className={paginationDotVariants({ variant, active: i === activeIndex })}
        />
      ))}
    </View>
  );
}
(PaginationDots as { displayName?: string }).displayName = 'PaginationDots';
